import { createEntityId } from '@/core/EntityId';
import type { UnitSystem } from '@/core/Units';
import type { EnvironmentState } from '@/environment/EnvironmentState';
import type { PlantGroup } from '@/editor/GroupManager';
import type { FaunaInstance } from '@/fauna/FaunaInstance';
import type { FloraInstance, Vec3 } from '@/flora/FloraInstance';
import type { SiteLocalization } from '@/localization/SiteLocalization';
import type { SemanticTag } from '@/semantics/SemanticTag';
import type { SiteObject } from '@/site/SiteObject';
import { hasGroundEdits } from '@/terrain/TerrainGenerator';
import type { SurfacePaletteState } from '@/terrain/TerrainSurface';
import type { TerrainProjectState, TerrainTemplateId } from '@/terrain/TerrainTypes';

/**
 * The canonical project document (spec §14, `docs/PROJECT_FILE_FORMAT.md`).
 *
 * This is the source of truth a site is saved as and rebuilt from — *not* a serialized Three.js
 * scene. Terrain is stored as its generation parameters (deterministic), plants as their canonical
 * instances, and the environment as its own serializable state. Loading reconstructs every view from
 * this data, so a saved site reopens identically without any renderer detail being persisted.
 */

/**
 * Bumped to 2 when `siteObjects` was added, to 3 when the terrain gained painted ground and
 * authoritative hand-graded heights, to 4 when site objects gained polygon footprints and multi-mass
 * buildings, to 5 when the site gained semantic tags, to 6 when the ground palette became
 * open — forged and uploaded materials, and the extra splat pages they need — and to 7 when a site
 * could say where on Earth it is.
 *
 * Older files still open unchanged, and each absence is a statement rather than a default: a v1 file
 * has no site objects, a v2 file has no paint, a v3 file's buildings are rectangular prisms — which
 * is exactly what `footprint`/`masses` being absent already means (see `migrateProjectV1ToV2`) — and
 * a v4 file has never been tagged, which is different from having had its tags removed, a v5
 * file's ground can only be painted with the five materials that shipped in code, a v6 file has
 * never been localized, which is different from having been localized to nowhere, and a v7 file's
 * tags are words alone — they carry no reference photograph, which is different from having had one
 * removed, and a v8 file has no fauna, because there was nothing to place — which is different from
 * a site whose creatures were all deleted.
 * The version exists so a *newer* file is refused rather than silently stripped of its ground.
 */
export const PROJECT_SCHEMA_VERSION = 9;

/** Members of an alias group, materialized for the file (the link also lives on each instance). */
export interface AliasGroup {
  id: string;
  memberIds: string[];
}

/** A named camera framing stored with the project (populated in milestone 8.2). */
export interface SavedCameraView {
  id: string;
  name: string;
  position: Vec3;
  target: Vec3;
  fovDegrees: number;
}

export interface LandscapeProject {
  schemaVersion: number;
  id: string;
  name: string;
  description?: string;
  /** ISO 8601. */
  createdAt: string;
  /** ISO 8601. */
  updatedAt: string;
  units: UnitSystem;
  terrain: TerrainProjectState;
  /**
   * Ground materials this site added beyond the five that ship in code (schema 6+).
   *
   * Carried in the document rather than in a shared library because a material is part of the
   * design: a site painted with a particular red gravel has to open on another machine, and a
   * central library would mean either shipping every material anyone ever forged or opening the
   * project to a palette of grey rectangles. The swatch image travels inline as a data URL, which
   * is what makes the file self-contained.
   */
  surfacePalette?: SurfacePaletteState;
  environment: EnvironmentState;
  floraInstances: FloraInstance[];
  /**
   * Buildings, paving, and props (schema 2+). Everything built is represented as a record here rather
   * than as imported geometry — which is also what lets the capture manifest tell an image model that
   * there is a shed in the frame.
   *
   * A prop record names an asset by id rather than generating its own form, but it is still a record:
   * the mesh lives in the library, never in the document. See `SiteObjectModelSource`.
   */
  siteObjects: SiteObject[];
  /**
   * Placed creatures and figures (schema 9+).
   *
   * A separate list rather than a `kind` on `siteObjects`, because a creature is not a site object in
   * any way that matters: it has no footprint, refuses nothing, is not levelled into the slope, and
   * carries a pose instead of a roof. Folding it in would have meant a record where two thirds of the
   * fields are inapplicable — which is the shape `SiteObject` already strains under for props.
   *
   * Absent in a v8 file, and that is a statement: the document predates fauna, so it has none. See
   * `docs/FAUNA_SCHEMA.md`.
   */
  faunaInstances: FaunaInstance[];
  groups: PlantGroup[];
  aliasGroups: AliasGroup[];
  cameraViews: SavedCameraView[];
  /**
   * What the designer has said the site *means* (schema 5+).
   *
   * One flat list rather than a field on each record, because a tag can be about a place, and a place
   * owns no record — see `semantics/SemanticTag.ts`. Tags pointing at a plant or building that is no
   * longer in the document are dropped on the way out rather than written as dangling references.
   */
  semanticTags: SemanticTag[];
  /**
   * Where this site is, as environmental parameters (schema 7+).
   *
   * A *profile*, and nothing derived from it. No resolved bloom date, no suitability rating, no
   * localized copy of a single flora record is written here or anywhere else in the document — those
   * are recomputed from this profile and the canonical catalog every time the site opens. Persisting
   * them would be the same mistake as shipping one flora record per zone: a copy that goes stale the
   * moment the catalog is corrected, in a file nobody thinks to look in.
   *
   * Absent means the site has never been localized, which is not the same as being localized to the
   * default climate — the first draws no badges and raises no warnings, the second would claim to
   * have checked. See `localization/SiteLocalization.ts`.
   */
  localization?: SiteLocalization;
  floraCatalogVersion: string;
}

/**
 * The stable identity of the document being edited. Held by the UI across saves so pressing Save
 * twice updates one record instead of creating a second copy.
 */
export interface ProjectIdentity {
  id: string;
  name: string;
  /** ISO 8601. */
  createdAt: string;
}

export const DEFAULT_PROJECT_NAME = 'Untitled site';

/**
 * Whether the document holds nothing worth keeping a record of.
 *
 * Autosave asks this before it mints a record for a site the user has never named (milestone 7.3):
 * opening the app, nudging the sun, and closing the tab must not leave an "Untitled site" behind, and
 * doing it every session must not leave a library of them.
 *
 * *Content* is what counts — plants, buildings, tags, groups, saved views, painted or hand-graded
 * ground, a forged material, a description. The environment deliberately does not: sun, season
 * and weather have a value on every site from the moment it is created, so treating a moved slider as
 * content would make every empty site look full. Nor does the terrain *template*, which is likewise
 * chosen for you — a generated landscape nobody has touched is still a blank sheet, and once anything
 * is placed on it the site stops being empty by every other clause here.
 */
export function isEmptyProject(project: LandscapeProject): boolean {
  if (project.floraInstances.length > 0) return false;
  if (project.siteObjects.length > 0) return false;
  if (project.faunaInstances.length > 0) return false;
  if (project.groups.length > 0 || project.aliasGroups.length > 0) return false;
  if (project.semanticTags.length > 0) return false;
  if (project.cameraViews.length > 0) return false;
  if ((project.description ?? '').trim().length > 0) return false;
  // A location deliberately does *not* count. It used to, on the grounds that saying where a site is
  // is a decision about it — but the app now works out where you are by itself when the browser will
  // say, and a profile it chose is exactly the kind of thing this function already excludes for the
  // season and the terrain template. A hand-typed ZIP still marks the document dirty, so it is saved
  // with a site that has any other content; what it must not do is mint an "Untitled site" out of an
  // empty session nobody touched.
  // A material forged in the texture studio is work even before it is brushed onto anything.
  if ((project.surfacePalette?.layers.length ?? 0) > 0) return false;
  if (project.terrain.surface) return false;
  return !hasGroundEdits(project.terrain);
}

/**
 * `desired`, or `desired` with the lowest free index appended, so no two records share a name.
 *
 * Used for the placeholder name autosave gives a draft, where a collision is otherwise the norm
 * rather than the exception — every unnamed site starts life called the same thing. A name that
 * already ends in an index is re-indexed rather than stacked, so naming a copy of "Front garden 2"
 * gives "Front garden 3" instead of "Front garden 2 2".
 */
export function uniqueProjectName(desired: string, taken: Iterable<string>): string {
  const wanted = desired.trim() || DEFAULT_PROJECT_NAME;
  const used = new Set<string>();
  for (const name of taken) used.add(name.trim().toLowerCase());
  if (!used.has(wanted.toLowerCase())) return wanted;

  const base = wanted.replace(/\s+\d+$/, '').trim() || wanted;
  for (let index = 2; index < 10_000; index += 1) {
    const candidate = `${base} ${index}`;
    if (!used.has(candidate.toLowerCase())) return candidate;
  }
  // Unreachable in practice; a timestamp still beats returning a duplicate.
  return `${base} ${Date.now()}`;
}

/** Start a fresh document identity (a site that has never been saved). */
export function newProjectIdentity(name: string = DEFAULT_PROJECT_NAME): ProjectIdentity {
  return {
    id: createEntityId('project-'),
    name: name.trim() || DEFAULT_PROJECT_NAME,
    createdAt: new Date().toISOString(),
  };
}

/** The identity of an existing document, so a loaded project keeps saving over itself. */
export function identityOf(project: LandscapeProject): ProjectIdentity {
  return { id: project.id, name: project.name, createdAt: project.createdAt };
}

/**
 * Alias groups implied by the instances. The link is stored on each plant, so the file's
 * `aliasGroups` array is derived rather than tracked separately — it cannot drift out of sync.
 */
export function deriveAliasGroups(instances: readonly FloraInstance[]): AliasGroup[] {
  const byGroup = new Map<string, string[]>();
  for (const instance of instances) {
    if (instance.aliasGroupId === undefined) continue;
    const members = byGroup.get(instance.aliasGroupId) ?? [];
    members.push(instance.id);
    byGroup.set(instance.aliasGroupId, members);
  }
  return [...byGroup.entries()].map(([id, memberIds]) => ({ id, memberIds }));
}

/** What actually landed when a project was applied to the running app. */
export interface ProjectLoadReport {
  plantCount: number;
  /** Plants whose species is not in this build's catalog; reported rather than silently dropped. */
  skippedPlantCount: number;
  /** Buildings and paving restored with the site. */
  siteObjectCount: number;
  /** Creatures and figures restored with the site. */
  faunaCount: number;
  /** Fauna whose record is not in this build's catalog; reported rather than silently dropped. */
  skippedFaunaCount: number;
  /** Semantic tags restored with the site. */
  tagCount: number;
}

/**
 * Whether a tag still points at something that exists.
 *
 * Used at the save boundary rather than eagerly on delete: pruning a tag the moment its plant is
 * removed would make undoing the delete restore a plant stripped of everything the designer had said
 * about it. Filtering on the way out keeps the live pairing intact while the file stays clean.
 */
export function tagTargetExists(
  tag: SemanticTag,
  livePlantIds: ReadonlySet<string>,
  liveObjectIds: ReadonlySet<string>,
): boolean {
  if (tag.target.kind === 'plant') return livePlantIds.has(tag.target.instanceId);
  if (tag.target.kind === 'structure') return liveObjectIds.has(tag.target.objectId);
  return true;
}

export interface ProjectSummary {
  plantCount: number;
  speciesCount: number;
  groupCount: number;
  siteObjectCount: number;
  faunaCount: number;
  tagCount: number;
  templateId: TerrainTemplateId;
  widthMeters: number;
  lengthMeters: number;
}

/** Counts used by the save/open dialogs to describe a document without loading it. */
export function summarizeProject(project: LandscapeProject): ProjectSummary {
  return {
    plantCount: project.floraInstances.length,
    speciesCount: new Set(project.floraInstances.map((i) => i.floraDefinitionId)).size,
    groupCount: project.groups.length,
    siteObjectCount: project.siteObjects.length,
    faunaCount: project.faunaInstances.length,
    tagCount: project.semanticTags.length,
    templateId: project.terrain.generation.templateId,
    widthMeters: project.terrain.generation.widthMeters,
    lengthMeters: project.terrain.generation.lengthMeters,
  };
}

/** Filename-safe slug for `.landscape.json` downloads. */
export function projectFileName(project: LandscapeProject): string {
  const slug = project.name
    .toLowerCase()
    .replace(/[^\w-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'site'}.landscape.json`;
}
